import { useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, X, Trash2, KeyRound, Layers, Pencil, MapPin, LockKeyhole, IdCard } from "lucide-react";
import { StaffProfileDialog } from "../components/StaffProfile";
import { UserAvatar } from "../components/UserAvatar";
import { RgpdExportButton } from "../components/RgpdExportButton";
import { SetPasswordDialog } from "../components/SetPasswordDialog";
import { SECTIONS } from "../lib/sections";
import { SitesAccessModal } from "../components/SitesAccessModal";

function useProfiles() {
  const [profiles, setProfiles] = useState([]);
  const load = () => http.get("/permission-profiles").then((r) => setProfiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return [profiles, load];
}

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

function PermissionGrid({ value, onChange, profiles }) {
  const set = new Set(value || []);
  const toggle = (c) => { const n = new Set(set); n.has(c) ? n.delete(c) : n.add(c); onChange([...n]); };
  return (
    <div data-testid="perm-grid">
      {profiles?.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Appliquer un profil</div>
          <div className="flex flex-wrap gap-1.5">
            {profiles.map((p) => {
              const on = sameSet(value || [], p.permissions);
              return (
                <button type="button" key={p.id} data-testid={`apply-profile-${p.id}`} onClick={() => onChange([...p.permissions])} title={SECTIONS.filter((s) => p.permissions.includes(s.code)).map((s) => s.label).join(", ")}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-indigo-400"}`}>
                  <Layers className="h-3 w-3" />{p.name}<span className={on ? "text-indigo-100" : "text-slate-400"}>{p.permissions.length}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex gap-3 mb-2 text-xs">
        <button type="button" data-testid="perm-all" onClick={() => onChange(SECTIONS.map((s) => s.code))} className="font-semibold text-sky-700 hover:underline">Tout cocher</button>
        <button type="button" data-testid="perm-none" onClick={() => onChange([])} className="font-semibold text-slate-500 hover:underline">Tout décocher</button>
        <span className="ml-auto text-slate-500">{set.size}/{SECTIONS.length} rubriques</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {SECTIONS.map((s) => (
          <label key={s.code} className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer select-none transition-colors ${set.has(s.code) ? "border-sky-300 bg-sky-50 text-sky-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            <input type="checkbox" data-testid={`perm-${s.code}`} checked={set.has(s.code)} onChange={() => toggle(s.code)} className="h-4 w-4" />{s.label}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Users() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [permFor, setPermFor] = useState(null);
  const [sitesFor, setSitesFor] = useState(null);
  const [pwdFor, setPwdFor] = useState(null);
  const [staffFor, setStaffFor] = useState(null);
  const [profiles, loadProfiles] = useProfiles();
  const [editProfile, setEditProfile] = useState(null);
  const { user } = useAuth();

  const load = async () => { const r = await http.get("/auth/users"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Utilisateurs</h1>
          <p className="text-sm text-slate-600">Gérez les accès administrateurs et leur fiche salarié (registre du personnel, diplômes)</p>
        </div>
        <button data-testid="add-user" onClick={()=>setCreating(true)} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md text-sm font-semibold"><Plus className="h-4 w-4"/> Nouvel accès</button>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <th className="px-4 py-3">Nom</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Rôle</th><th className="px-4 py-3">Accès</th><th className="px-4 py-3">Créé le</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-3 font-semibold"><div className="flex items-center gap-2.5"><UserAvatar u={u} size={30} testid={`user-avatar-${u.id}`} /><span>{u.name}</span></div>
                  {u.staff_agent_id && <div data-testid={`user-staff-badge-${u.id}`} className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700"><IdCard className="h-3 w-3" /> Fiche salarié</div>}
                </td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${u.role==="admin"?"bg-indigo-100 text-indigo-800 border border-indigo-200":"bg-sky-100 text-sky-800 border border-sky-200"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`user-access-${u.id}`}>
                  {u.role === "admin" || u.permissions == null
                    ? <span className="text-emerald-700 font-semibold">Toutes les rubriques</span>
                    : u.permissions.length === 0
                      ? <span className="text-rose-700 font-semibold">Aucune rubrique</span>
                      : <span className="text-slate-700" title={SECTIONS.filter((s) => u.permissions.includes(s.code)).map((s) => s.label).join(", ")}>{u.permissions.length} rubrique(s) : {SECTIONS.filter((s) => u.permissions.includes(s.code)).slice(0, 3).map((s) => s.label).join(", ")}{u.permissions.length > 3 ? "…" : ""}</span>}
                  {u.role !== "admin" && <div className="mt-0.5 text-slate-500" data-testid={`user-sites-${u.id}`}><MapPin className="h-3 w-3 inline -mt-0.5" /> {u.site_ids == null ? "Tous les sites" : `${u.site_ids.length} site(s)`}</div>}
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="px-4 py-3 text-right min-w-[330px]"><div className="flex flex-wrap justify-end gap-y-1 max-w-[360px] ml-auto">
                  {(user?.role === "admin" || u.id === user?.id) && <button data-testid={`staff-user-${u.id}`} onClick={() => setStaffFor(u)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-emerald-700 hover:bg-emerald-50 mr-1"><IdCard className="h-4 w-4" /> Fiche salarié</button>}
                  <span className="mr-1"><RgpdExportButton url={`/auth/users/${u.id}/rgpd-export.pdf`} testid={`rgpd-user-${u.id}`} /></span>
                  {u.role !== "admin" && <button data-testid={`perm-user-${u.id}`} onClick={() => setPermFor(u)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-sky-700 hover:bg-sky-50 mr-1"><KeyRound className="h-4 w-4" /> Droits d'accès</button>}
                  {u.role !== "admin" && <button data-testid={`sites-user-${u.id}`} onClick={() => setSitesFor(u)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-sky-700 hover:bg-sky-50 mr-1"><MapPin className="h-4 w-4" /> Sites</button>}
                  {u.id !== user?.id && <button data-testid={`password-user-${u.id}`} onClick={() => setPwdFor(u)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-sky-700 hover:bg-sky-50 mr-1"><LockKeyhole className="h-4 w-4" /> Mot de passe</button>}
                  {u.id !== user?.id && (
                    <button data-testid={`delete-user-${u.id}`} onClick={async()=>{ if(window.confirm(`Supprimer ${u.name} ?`)){ await http.delete(`/auth/users/${u.id}`); load();} }} className="p-1.5 rounded hover:bg-rose-50"><Trash2 className="h-4 w-4 text-rose-600"/></button>
                  )}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProfilesCard profiles={profiles} onEdit={setEditProfile} onChanged={loadProfiles} />

      {creating && <UserModal profiles={profiles} onClose={()=>setCreating(false)} onSaved={load}/>}
      {sitesFor && <SitesAccessModal u={sitesFor} onClose={() => setSitesFor(null)} onSaved={load} />}
      {staffFor && <StaffProfileDialog uid={staffFor.id === user?.id ? "me" : staffFor.id} onClose={() => { setStaffFor(null); load(); }} onSaved={load} />}
      {pwdFor && <SetPasswordDialog target={pwdFor} endpoint={`/auth/users/${pwdFor.id}/password`} onClose={() => setPwdFor(null)} />}
      {permFor && <PermModal u={permFor} profiles={profiles} onClose={() => setPermFor(null)} onSaved={load} />}
      {editProfile && <ProfileModal p={editProfile} onClose={() => setEditProfile(null)} onSaved={loadProfiles} />}
    </div>
  );
}

function UserModal({ profiles, onClose, onSaved }) {
  const [v, setV] = useState({ name:"", email:"", password:"", role:"secondary" });
  const [perms, setPerms] = useState([]);
  const [err, setErr] = useState("");
  const save = async () => {
    try {
      const r = await http.post("/auth/users", v);
      if (v.role !== "admin") await http.put(`/auth/users/${r.data.id}/permissions`, { permissions: perms });
      onSaved(); onClose();
    }
    catch (e) { setErr(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[95vh] overflow-auto border border-slate-200 animate-fade-in-up" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-xl font-bold">Nouvel accès administrateur</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          <L label="Nom complet *"><input data-testid="user-name" className="input" value={v.name} onChange={(e)=>setV({...v, name:e.target.value})}/></L>
          <L label="Email *"><input data-testid="user-email" type="email" className="input" value={v.email} onChange={(e)=>setV({...v, email:e.target.value})}/></L>
          <L label="Mot de passe * (10 caractères min., lettres et chiffres)"><input data-testid="user-password" type="password" className="input" value={v.password} onChange={(e)=>setV({...v, password:e.target.value})}/></L>
          <L label="Rôle">
            <select className="input" value={v.role} onChange={(e)=>setV({...v, role:e.target.value})}>
              <option value="secondary">Administrateur secondaire</option>
              <option value="admin">Administrateur principal</option>
            </select>
          </L>
          {v.role !== "admin" && <L label="Rubriques autorisées"><PermissionGrid value={perms} onChange={setPerms} profiles={profiles} /></L>}
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="user-save" onClick={save} className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md">Créer</button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}
function L({label, children}) { return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>; }

function PermModal({ u, profiles, onClose, onSaved }) {
  const { user: me } = useAuth();
  const [perms, setPerms] = useState(u.permissions == null ? SECTIONS.map((s) => s.code) : u.permissions);
  const [chans, setChans] = useState(u.channels || ["general"]);
  const [err, setErr] = useState("");
  const [eqVal, setEqVal] = useState(!!u.equipment_validator);
  const [allChans, setAllChans] = useState([["general", "Général"], ["rh", "RH"], ["commercial", "Commercial"], ["exploitation", "Exploitation"]]);
  useEffect(() => { if (me?.is_primary_admin) http.get("/messages/channels").then((r) => setAllChans(r.data.map((c) => [c.key, c.label]))).catch(() => {}); }, [me?.is_primary_admin]);
  const save = async () => {
    try {
      await http.put(`/auth/users/${u.id}/permissions`, { permissions: perms });
      if (me?.is_primary_admin) await http.put(`/auth/users/${u.id}/channels`, { channels: chans });
      if (eqVal !== !!u.equipment_validator) await http.put(`/auth/users/${u.id}/equipment-validator`, { value: eqVal });
      onSaved(); onClose();
    }
    catch (e) { setErr(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="perm-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-lg font-bold">Droits d'accès — {u.name}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-600">Les rubriques non cochées sont masquées dans le menu et refusées par le serveur, même via un lien direct.</p>
          <PermissionGrid value={perms} onChange={setPerms} profiles={profiles} />
          <label className="flex items-start gap-2 text-sm border border-amber-200 bg-amber-50/50 rounded-md px-3 py-2 cursor-pointer" data-testid="perm-eqval-label">
            <input data-testid="perm-eqval-checkbox" type="checkbox" className="mt-0.5" checked={eqVal} onChange={(e) => setEqVal(e.target.checked)} />
            <span><b>Responsable équipements</b><span className="block text-xs text-slate-500">Valide les besoins en équipement et les bons de commande</span></span>
          </label>
          {me?.is_primary_admin && <div data-testid="channels-grid">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Canaux de messagerie</div>
            <div className="flex flex-wrap gap-2">
              {allChans.map(([k, l]) => (
                <label key={k} className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-md px-2.5 py-1.5 cursor-pointer">
                  <input data-testid={`channel-${k}`} type="checkbox" checked={chans.includes(k)} onChange={(e) => setChans(e.target.checked ? [...chans, k] : chans.filter((c) => c !== k))} />{l}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Les messages des agents envoyés au service RH, Commercial ou Exploitation arrivent aux membres du canal correspondant.</p>
          </div>}
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="perm-save" onClick={save} className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function ProfilesCard({ profiles, onEdit, onChanged }) {
  const del = async (p) => { if (window.confirm(`Supprimer le profil « ${p.name} » ? Les utilisateurs gardent leurs droits actuels.`)) { await http.delete(`/permission-profiles/${p.id}`); onChanged(); } };
  return (
    <div className="mt-8 border border-slate-200 bg-white rounded-md" data-testid="profiles-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div>
          <div className="font-display font-bold flex items-center gap-2"><Layers className="h-4 w-4 text-indigo-600" /> Profils de droits</div>
          <div className="text-xs text-slate-500">Modèles de rubriques à appliquer en un clic. Modifier un profil ne change pas les droits des utilisateurs existants.</div>
        </div>
        <button data-testid="add-profile" onClick={() => onEdit({ name: "", permissions: [] })} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-indigo-300 text-indigo-700 hover:bg-indigo-50"><Plus className="h-3.5 w-3.5" /> Nouveau profil</button>
      </div>
      <div className="divide-y divide-slate-100">
        {profiles.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`profile-${p.id}`}>
            <div className="w-36 font-semibold text-sm">{p.name}</div>
            <div className="flex-1 flex flex-wrap gap-1">
              {SECTIONS.filter((s) => p.permissions.includes(s.code)).map((s) => <span key={s.code} className="text-[10px] font-semibold rounded bg-slate-100 text-slate-700 px-1.5 py-0.5">{s.label}</span>)}
              {!p.permissions.length && <span className="text-xs text-slate-400 italic">Aucune rubrique</span>}
            </div>
            <button data-testid={`edit-profile-${p.id}`} onClick={() => onEdit(p)} className="p-1.5 rounded hover:bg-slate-100" title="Modifier"><Pencil className="h-4 w-4 text-slate-600" /></button>
            <button data-testid={`delete-profile-${p.id}`} onClick={() => del(p)} className="p-1.5 rounded hover:bg-rose-50" title="Supprimer"><Trash2 className="h-4 w-4 text-rose-600" /></button>
          </div>
        ))}
        {!profiles.length && <div className="px-4 py-6 text-center text-sm text-slate-500">Aucun profil.</div>}
      </div>
    </div>
  );
}

function ProfileModal({ p, onClose, onSaved }) {
  const [name, setName] = useState(p.name);
  const [perms, setPerms] = useState(p.permissions);
  const [err, setErr] = useState("");
  const save = async () => {
    try {
      if (p.id) await http.put(`/permission-profiles/${p.id}`, { name, permissions: perms });
      else await http.post("/permission-profiles", { name, permissions: perms });
      onSaved(); onClose();
    } catch (e) { setErr(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="profile-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-lg font-bold">{p.id ? "Modifier le profil" : "Nouveau profil"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <L label="Nom du profil *"><input data-testid="profile-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. : Comptabilité" /></L>
          <L label="Rubriques"><PermissionGrid value={perms} onChange={setPerms} /></L>
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="profile-save" onClick={save} className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md">Enregistrer</button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}
